import { useRef, useEffect, useMemo } from 'react';
import { cn } from '@/utils/cn';
import type { PersonInfo } from '@/types/api';

// Per-person accent colors — index 0..4, wraps for larger counts
const PERSON_COLORS: { border: string; fill: string; hex: string }[] = [
  { border: 'border-blue-400',   fill: 'ring-blue-400',   hex: '#60a5fa' },
  { border: 'border-green-400',  fill: 'ring-green-400',  hex: '#4ade80' },
  { border: 'border-orange-400', fill: 'ring-orange-400', hex: '#fb923c' },
  { border: 'border-purple-400', fill: 'ring-purple-400', hex: '#c084fc' },
  { border: 'border-pink-400',   fill: 'ring-pink-400',   hex: '#f472b6' },
];

function colorFor(index: number) {
  return PERSON_COLORS[index % PERSON_COLORS.length];
}

// ---------------------------------------------------------------------------
// BboxOverlayCanvas
// ---------------------------------------------------------------------------

interface BboxOverlayCanvasProps {
  sampleFrameUrl: string;
  persons: PersonInfo[];
  selectedIndex: number | null;
}

/**
 * Renders the full sample frame with bounding box overlays drawn on a canvas
 * positioned over the image. The selected person's box is drawn thicker with a
 * semi-transparent fill.
 */
function BboxOverlayCanvas({ sampleFrameUrl, persons, selectedIndex }: BboxOverlayCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  function draw() {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || img.naturalWidth === 0) return;

    const { offsetWidth: w, offsetHeight: h } = img;
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, w, h);

    persons.forEach(({ person_index, bbox }) => {
      const [x1, y1, x2, y2] = bbox;
      const px = x1 * w;
      const py = y1 * h;
      const pw = (x2 - x1) * w;
      const ph = (y2 - y1) * h;

      const { hex } = colorFor(person_index);
      const isSelected = person_index === selectedIndex;

      ctx.save();

      // Semi-transparent fill for selected person
      if (isSelected) {
        ctx.fillStyle = hex + '33'; // 20% opacity
        ctx.fillRect(px, py, pw, ph);
      }

      // Bounding box stroke
      ctx.strokeStyle = hex;
      ctx.lineWidth = isSelected ? 3 : 1.5;
      ctx.strokeRect(px, py, pw, ph);

      // Numbered badge — filled circle at top-left of bbox with the person number.
      // Circle + white ring + drop shadow ensures legibility on any video background.
      const badgeR = Math.max(12, Math.min(18, w * 0.022));
      const badgeCx = px + badgeR + 2;
      const badgeCy = py + badgeR + 2;
      const badgeLabel = String(person_index + 1);

      ctx.shadowColor = 'rgba(0, 0, 0, 0.75)';
      ctx.shadowBlur = 6;
      ctx.shadowOffsetY = 1;

      ctx.beginPath();
      ctx.arc(badgeCx, badgeCy, badgeR, 0, Math.PI * 2);
      ctx.fillStyle = hex;
      ctx.fill();

      ctx.strokeStyle = 'rgba(255,255,255,0.55)';
      ctx.lineWidth = 1.25;
      ctx.stroke();

      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      const badgeFont = Math.round(badgeR * 1.1);
      ctx.font = `700 ${badgeFont}px Inter, sans-serif`; // PT-3: Sora was never loaded
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.strokeText(badgeLabel, badgeCx, badgeCy);

      ctx.fillStyle = '#ffffff';
      ctx.fillText(badgeLabel, badgeCx, badgeCy);

      ctx.restore();
    });
  }

  // Redraw whenever the image loads or selections change
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    if (img.complete) {
      draw();
    } else {
      img.onload = draw;
    }
  });

  return (
    <div className="relative w-full rounded-lg overflow-hidden bg-black">
      <img
        ref={imgRef}
        src={sampleFrameUrl}
        alt="Sample frame with detected persons"
        className="block w-full h-auto"
        onLoad={draw}
      />
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PersonThumbnail
// ---------------------------------------------------------------------------

interface PersonThumbnailProps {
  person: PersonInfo;
  isSelected: boolean;
  onSelect: () => void;
}

function PersonThumbnail({ person, isSelected, onSelect }: PersonThumbnailProps) {
  const { border, fill, hex } = colorFor(person.person_index);
  const confidencePct = Math.round(person.confidence * 100);

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      aria-label={`Select Person ${person.person_index + 1}, confidence ${confidencePct}%`}
      className={cn(
        'relative flex flex-col items-center gap-1.5 p-1.5 rounded-lg border-2 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0F14]',
        'hover:bg-white/5 cursor-pointer',
        isSelected
          ? cn('bg-white/8 shadow-lg', border, `focus-visible:${fill}`)
          : 'border-white/15 hover:border-white/30'
      )}
      style={isSelected ? { boxShadow: `0 0 12px ${hex}55` } : undefined}
    >
      {/* Crop thumbnail or placeholder */}
      <div
        className={cn(
          'relative w-16 h-24 rounded overflow-hidden border-2 flex items-center justify-center bg-[#1a1f28]',
          isSelected ? border : 'border-transparent'
        )}
      >
        {person.crop_data_url ? (
          <img
            src={person.crop_data_url}
            alt={`Person ${person.person_index + 1} crop`}
            className="w-full h-full object-cover"
          />
        ) : (
          <svg
            className="w-8 h-8 text-white/25"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
          </svg>
        )}
        {/* Face-not-visible badge — belt-and-suspenders: server normally filters
            these out before sending; shown here for any that slip through in-flight. */}
        {person.face_visible === false && (
          <span
            className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-center text-[8px] font-semibold leading-tight bg-amber-500/80 text-white"
            data-testid={`face-not-visible-badge-${person.person_index}`}
            aria-label="Face not visible"
          >
            face not visible
          </span>
        )}
        {/* Numbered badge — same color as the canvas overlay so the user can match thumbnail to in-frame person */}
        <span
          className="absolute top-1 left-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white select-none"
          style={{
            backgroundColor: hex,
            boxShadow: '0 1px 4px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.25)',
          }}
          aria-hidden="true"
        >
          {person.person_index + 1}
        </span>
      </div>

      {/* Label */}
      <span className="text-xs font-medium text-text-primary">
        Person {person.person_index + 1}
      </span>

      {/* Confidence badge */}
      <span
        className={cn(
          'text-[10px] font-semibold px-1.5 py-0.5 rounded-full',
          confidencePct >= 85
            ? 'bg-success/20 text-success'
            : confidencePct >= 65
            ? 'bg-warning/20 text-warning'
            : 'bg-danger/20 text-danger'
        )}
      >
        {confidencePct}%
      </span>

      {/* Recommended badge — shown only when the backend marks this candidate as dominant */}
      {person.recommended && (
        <span
          className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-accent/20 text-accent border border-accent/30 leading-tight"
          data-testid={`recommended-badge-${person.person_index}`}
          aria-label="Recommended"
        >
          Recommended
        </span>
      )}

      {/* Selected checkmark overlay */}
      {isSelected && (
        <span
          className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 rounded-full bg-white/90"
          aria-hidden="true"
          style={{ color: hex }}
        >
          <svg viewBox="0 0 12 12" fill="currentColor" className="w-3 h-3">
            <path d="M1.5 6l3 3 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// PersonPicker (main export)
// ---------------------------------------------------------------------------

export interface PersonPickerProps {
  videoId: string;
  persons: PersonInfo[];
  sampleFrameUrl?: string;
  selectedIndex: number | null;
  onSelect: (personIndex: number) => void;
  /** Contextual label shown in the header, e.g. "Select the coach" */
  label?: string;
  className?: string;
}

/**
 * PersonPicker — lets the user choose which tracked person in a multi-person
 * video is the subject of interest (coach or athlete).
 *
 * Renders nothing and silently calls onSelect(0) when there is only one person.
 * The parent should call this component only after checking person_count > 1,
 * or pass the full persons list and let the component self-manage.
 *
 * Phase 2b additions:
 * - Candidates are displayed sorted by rank_score descending (highest quality first).
 * - On initial mount the highest-rank_score candidate is auto-selected (only if
 *   selectedIndex is still null — existing manual selections are never overridden).
 * - The candidate with recommended===true gets a "Recommended" badge.
 * - person_index identity is preserved for all selection callbacks and React keys.
 */
export default function PersonPicker({
  persons,
  sampleFrameUrl,
  selectedIndex,
  onSelect,
  label,
  className,
}: PersonPickerProps) {
  // Sort candidates by rank_score descending for display.
  // We derive this BEFORE the single-person early return so the sorted list is
  // always available; the sort is memoised to avoid re-allocating on every render.
  const sortedPersons = useMemo(
    () => [...persons].sort((a, b) => b.rank_score - a.rank_score),
    [persons]
  );

  // Auto-select the top-ranked candidate on initial load.
  // Only fires when selectedIndex is null (i.e., the user hasn't chosen yet).
  // Uses useEffect so it runs after mount and doesn't side-effect during render.
  useEffect(() => {
    if (persons.length >= 2 && selectedIndex === null && sortedPersons.length > 0) {
      onSelect(sortedPersons[0].person_index);
    }
  // onSelect is intentionally omitted: the parent may pass an inline arrow
  // function, and re-running when it changes would override manual selections.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persons.length, selectedIndex, sortedPersons]);

  // Auto-select and hide when there is exactly one person
  if (persons.length === 1) {
    if (selectedIndex !== 0) {
      onSelect(0);
    }
    return null;
  }

  // No persons detected yet — nothing to show
  if (persons.length === 0) {
    return null;
  }

  const headerText = label ?? 'Multiple people detected — select the person to evaluate';

  return (
    <div
      className={cn(
        'rounded-xl border border-white/10 bg-[#0d1117] p-4 space-y-4',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <svg
          className="w-4 h-4 text-text-muted flex-shrink-0"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zm8 0a3 3 0 11-6 0 3 3 0 016 0zM5.5 14a4.5 4.5 0 019 0v.5H5.5V14zM1 14a5.5 5.5 0 0111 0v.5H1V14z" />
        </svg>
        <p className="text-sm font-medium text-text-secondary">{headerText}</p>
      </div>

      {/* Thumbnail row — rendered in rank_score descending order (highest quality first).
          React key and onSelect use person_index (stable backend identity), NOT the sort position. */}
      <div
        className="flex flex-wrap gap-3"
        role="group"
        aria-label="Detected persons"
      >
        {sortedPersons.map((person) => (
          <PersonThumbnail
            key={person.person_index}
            person={person}
            isSelected={selectedIndex === person.person_index}
            onSelect={() => onSelect(person.person_index)}
          />
        ))}
      </div>

      {/* Full-frame bbox overlay */}
      {sampleFrameUrl && (
        <div className="space-y-1.5">
          <p className="text-xs text-text-muted">Sample frame — detected persons highlighted</p>
          <BboxOverlayCanvas
            sampleFrameUrl={sampleFrameUrl}
            persons={persons}
            selectedIndex={selectedIndex}
          />
        </div>
      )}

      {/* Selection summary */}
      {selectedIndex !== null && (
        <p className="text-xs text-text-muted">
          <span className="text-text-secondary font-medium">
            Person {selectedIndex + 1}
          </span>{' '}
          selected
          {persons.find((p) => p.person_index === selectedIndex) && (
            <> &mdash; {Math.round(persons.find((p) => p.person_index === selectedIndex)!.confidence * 100)}% detection confidence</>
          )}
        </p>
      )}
    </div>
  );
}
